import React, { useState, useEffect } from 'react';
import CsvImportDialog from './CsvImportDialog';
import BulkEnrollmentDialog from './BulkEnrollmentDialog';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    LinearProgress,
    Tabs,
    Tab,
    TableSortLabel,
    Autocomplete
} from '@mui/material';
import {
    PersonAdd as EnrollIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    FilterList as FilterIcon,
    GroupAdd as BulkEnrollIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';

interface Enrollment {
    id: number;
    userId: number;
    userName?: string;
    userEmail?: string;
    status: string;
    progressPercentage: number;
    finalScore?: number;
    enrolledAt: string;
    startedAt?: string;
    completedAt?: string;
    totalTimeSpent?: number;
    course: any;
    courseName?: string;
}

interface Course {
    id: number;
    title: string;
    isLicensed?: boolean;
}

const EnrollmentManagement: React.FC = () => {
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab') === 'requests' ? 1 : 0;
    const [activeTab, setActiveTab] = useState(requestedTab);
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [pendingRequests, setPendingRequests] = useState<Enrollment[]>([]);
    const [courses, setCourses] = useState<Course[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<number | 'all' | ''>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [loading, setLoading] = useState(true);
    const [requestsLoading, setRequestsLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedCourseForEnroll, setSelectedCourseForEnroll] = useState<number | ''>('');
    const [orderBy, setOrderBy] = useState<string>('enrolledAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    const [bulkEnrollOpen, setBulkEnrollOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectingEnrollment, setRejectingEnrollment] = useState<Enrollment | null>(null);
    const [rejectMessage, setRejectMessage] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    useEffect(() => {
        loadCourses();
    }, []);

    useEffect(() => {
        setActiveTab(requestedTab);
    }, [requestedTab]);

    useEffect(() => {
        if (activeTab === 0 && selectedCourse) {
            loadEnrollments();
        } else if (activeTab === 1) {
            loadPendingRequests();
        }
    }, [activeTab, selectedCourse, statusFilter, orderBy, order, page, perPage]);

    const loadCourses = async () => {
        try {
            const { data } = await dataProvider.getList('courses', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'title', order: 'ASC' },
                filter: {}
            });
            let ownCourses: Course[] = (data as Course[]).map(c => ({ ...c, isLicensed: false }));

            // Also fetch licensed courses from other companies
            let licensedCourses: Course[] = [];
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/licensed-courses`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
                );
                if (response.ok) {
                    const result = await response.json();
                    const licensed: any[] = result.courses || [];
                    const referenceIds = new Set(licensed.filter(c => c.license?.type === 'reference').map(c => c.id));
                    ownCourses = ownCourses.map(c => referenceIds.has(c.id)
                        ? { ...c, title: `${c.title} (Lizenz)`, isLicensed: true }
                        : c);
                    const ownCourseIds = new Set(ownCourses.map(c => c.id));
                    licensedCourses = licensed
                        .filter((c: any) => !ownCourseIds.has(c.id))
                        .map((c: any) => ({ id: c.id, title: `${c.title} (Lizenz)`, isLicensed: true }));
                }
            } catch (e) {
                console.error('Failed to load licensed courses:', e);
            }

            const allCourses = [...ownCourses, ...licensedCourses].sort((a, b) => a.title.localeCompare(b.title));
            setCourses(allCourses);
            if (allCourses.length > 0) {
                setSelectedCourse('all');
            }
        } catch (error) {
            notify('Fehler beim Laden der Kurse', { type: 'error' });
        }
    };

    const loadUsers = async () => {
        try {
            const { data } = await dataProvider.getList('users', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'lastName', order: 'ASC' },
                filter: {}
            });
            setUsers(data);
        } catch (error) {
            notify('Fehler beim Laden der Benutzer', { type: 'error' });
        }
    };

    const loadEnrollments = async () => {
        if (!selectedCourse) return;

        setLoading(true);
        try {
            const filter: any = { showAll: 'true' };

            // Only filter by course if not "all"
            if (selectedCourse !== 'all') {
                filter['course.id'] = selectedCourse;
            }

            if (statusFilter !== 'all') {
                filter.status = statusFilter;
            }

            const { data: enrollmentsData, total: totalCount } = await dataProvider.getList('course_enrollments', {
                pagination: { page, perPage },
                sort: { field: orderBy, order: order.toUpperCase() as 'ASC' | 'DESC' },
                filter
            });
            setTotal(totalCount || 0);

            // Extract unique course IDs
            const getCourseId = (course: any): number => {
                if (typeof course === 'number') return course;
                if (course && typeof course === 'object' && course.id) return course.id;
                if (typeof course === 'string') {
                    const parts = course.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            const courseIds = Array.from(new Set(enrollmentsData.map((e: any) => getCourseId(e.course))));

            // Extract unique user IDs
            const getUserId = (userId: any): number => {
                if (typeof userId === 'number') return userId;
                if (typeof userId === 'string') {
                    const parts = userId.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            const userIds = Array.from(new Set(enrollmentsData.map((e: any) => getUserId(e.userId)).filter(id => id > 0)));

            // Load all course and user details in parallel
            const [coursesResult, usersResult] = await Promise.all([
                courseIds.length > 0 ? dataProvider.getMany('courses', { ids: courseIds }) : Promise.resolve({ data: [] }),
                userIds.length > 0 ? dataProvider.getMany('users', { ids: userIds }) : Promise.resolve({ data: [] })
            ]);

            const coursesData = coursesResult.data || coursesResult;
            const usersData = usersResult.data || usersResult;

            // Create maps
            const coursesMap = new Map(coursesData.map((c: any) => [c.id, c]));
            const usersMap = new Map(usersData.map((u: any) => [u.id, u]));

            // Merge course and user data into enrollments
            const enrichedEnrollments = enrollmentsData.map((enrollment: any) => {
                const courseId = getCourseId(enrollment.course);
                const userId = getUserId(enrollment.userId);
                const course = coursesMap.get(courseId);
                const user = usersMap.get(userId);
                const fullName = user ? ((user.firstName || '') + ' ' + (user.lastName || '')).trim() : '';

                return {
                    ...enrollment,
                    course: course || {
                        id: courseId,
                        title: 'Kurs nicht gefunden'
                    },
                    courseName: course?.title || 'Kurs nicht gefunden',
                    userName: enrollment.userName || fullName || user?.email || ('User ' + userId),
                    userEmail: enrollment.userEmail || user?.email || '',
                    finalScore: enrollment.score ?? enrollment.finalScore ?? null,
                    totalTimeSpent: enrollment.timeSpentSeconds ?? enrollment.totalTimeSpent ?? 0,
                };
            });

            setEnrollments(enrichedEnrollments as Enrollment[]);
        } catch (error) {
            notify('Fehler beim Laden der Einschreibungen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEnrollDialog = () => {
        loadUsers();
        setDialogOpen(true);
    };

    const handleEnroll = async () => {
        if (!selectedUser || !selectedCourseForEnroll) {
            notify('Bitte wählen Sie Benutzer und Kurs aus', { type: 'warning' });
            return;
        }

        const course = courses.find(c => c.id === selectedCourseForEnroll);

        try {
            if (course?.isLicensed) {
                // Use dedicated licensed courses enrollment endpoint
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/licensed-courses/${selectedCourseForEnroll}/enroll`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                        body: JSON.stringify({ userId: selectedUser.id }),
                    }
                );
                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    throw new Error(err.detail || 'Enrollment failed');
                }
            } else {
                await dataProvider.create('course_enrollments', {
                    data: {
                        userId: selectedUser.id,
                        userName: `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email,
                        userEmail: selectedUser.email,
                        course: `/api/courses/${selectedCourseForEnroll}`,
                        status: 'enrolled',
                        progressPercentage: 0
                    }
                });
            }
            notify('Benutzer erfolgreich eingeschrieben', { type: 'success' });
            setDialogOpen(false);
            setSelectedUser(null);
            setSelectedCourseForEnroll('');
            loadEnrollments();
        } catch (error: any) {
            const detail = error?.body?.detail || error?.message || '';
            if (detail.includes('already enrolled') || detail.includes('Already enrolled')) {
                notify('Dieser Benutzer ist bereits in diesem Kurs eingeschrieben.', { type: 'warning' });
            } else if (detail.includes('Self-enrollment is not allowed')) {
                notify('Selbsteinschreibung ist für diesen Kurs nicht erlaubt.', { type: 'error' });
            } else if (detail.includes('must complete the following courses')) {
                const courses = detail.split(': ').slice(1).join(': ');
                notify(`Voraussetzungen nicht erfüllt: ${courses}`, { type: 'error' });
            } else if (detail.includes('Cannot enroll')) {
                notify('Einschreibung nicht möglich: Keine gültige Lizenz für diesen Kurs.', { type: 'error' });
            } else {
                notify(detail || 'Fehler beim Einschreiben des Benutzers', { type: 'error' });
            }
        }
    };

    const handleDelete = async (enrollment: Enrollment) => {
        const id = enrollment.id;
        const confirmText = translate('enrollments.manage.deleteConfirm', {
            user: enrollment.userName || `User ${enrollment.userId}`,
            course: enrollment.courseName || '-',
        });
        if (!window.confirm(confirmText)) {
            return;
        }

        try {
            await dataProvider.delete('course_enrollments', {
                id,
                previousData: {} as any
            });
            notify('Einschreibung erfolgreich gelöscht', { type: 'success' });
            loadEnrollments();
        } catch (error) {
            notify('Fehler beim Löschen der Einschreibung', { type: 'error' });
        }
    };

    const loadPendingRequests = async () => {
        setRequestsLoading(true);
        try {
            const { data: requestsData } = await dataProvider.getList('course_enrollments', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'enrolledAt', order: 'DESC' },
                filter: { status: 'pending_approval' }
            });

            const getCourseId = (course: any): number => {
                if (typeof course === 'number') return course;
                if (course && typeof course === 'object' && course.id) return course.id;
                if (typeof course === 'string') {
                    const parts = course.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            const getUserId = (userId: any): number => {
                if (typeof userId === 'number') return userId;
                if (typeof userId === 'string') {
                    const parts = userId.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            const courseIds = Array.from(new Set(requestsData.map((e: any) => getCourseId(e.course))));
            const userIds = Array.from(new Set(requestsData.map((e: any) => getUserId(e.userId)).filter((id: number) => id > 0)));

            const [coursesResult, usersResult] = await Promise.all([
                courseIds.length > 0 ? dataProvider.getMany('courses', { ids: courseIds }) : Promise.resolve({ data: [] }),
                userIds.length > 0 ? dataProvider.getMany('users', { ids: userIds }) : Promise.resolve({ data: [] })
            ]);

            const coursesMap = new Map(coursesResult.data.map((c: any) => [c.id, c]));
            const usersMap = new Map(usersResult.data.map((u: any) => [u.id, u]));

            const enriched = requestsData.map((enrollment: any) => {
                const courseId = getCourseId(enrollment.course);
                const userId = getUserId(enrollment.userId);
                const course = coursesMap.get(courseId);
                const user = usersMap.get(userId);
                const fullName = user ? ((user.firstName || '') + ' ' + (user.lastName || '')).trim() : '';

                return {
                    ...enrollment,
                    course: course || { id: courseId, title: 'Kurs nicht gefunden' },
                    courseName: course?.title || 'Kurs nicht gefunden',
                    userName: fullName || user?.email || (`User ${userId}`)
                };
            });

            setPendingRequests(enriched as Enrollment[]);
        } catch (error) {
            notify('Fehler beim Laden der Einschreibungsanfragen', { type: 'error' });
        } finally {
            setRequestsLoading(false);
        }
    };

    const handleApprove = async (enrollment: Enrollment) => {
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        try {
            const response = await fetch(`${apiUrl}/api/enrollments/${enrollment.id}/approve`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) throw new Error('Fehler beim Genehmigen');
            notify(`Einschreibung von ${enrollment.userName} genehmigt`, { type: 'success' });
            loadPendingRequests();
        } catch (error) {
            notify('Fehler beim Genehmigen der Einschreibung', { type: 'error' });
        }
    };

    const handleOpenRejectDialog = (enrollment: Enrollment) => {
        setRejectingEnrollment(enrollment);
        setRejectMessage('');
        setRejectDialogOpen(true);
    };

    const handleReject = async () => {
        if (!rejectingEnrollment) return;
        const token = localStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        try {
            const response = await fetch(`${apiUrl}/api/enrollments/${rejectingEnrollment.id}/reject`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ message: rejectMessage || undefined })
            });
            if (!response.ok) throw new Error('Fehler beim Ablehnen');
            notify(`Einschreibung von ${rejectingEnrollment.userName} abgelehnt`, { type: 'success' });
            setRejectDialogOpen(false);
            setRejectingEnrollment(null);
            loadPendingRequests();
        } catch (error) {
            notify('Fehler beim Ablehnen der Einschreibung', { type: 'error' });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'primary';
            case 'failed':
                return 'error';
            case 'enrolled':
                return 'default';
            case 'pending_approval':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Abgeschlossen';
            case 'in_progress':
                return 'In Bearbeitung';
            case 'failed':
                return 'Nicht bestanden';
            case 'enrolled':
                return 'Eingeschrieben';
            case 'pending_approval':
                return 'Anfrage ausstehend';
            case 'rejected':
                return 'Abgelehnt';
            default:
                return status;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('de-DE');
    };

    const formatTime = (seconds?: number) => {
        if (!seconds) return '-';
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Client-side search filtering and name sorting
    const filteredEnrollments = React.useMemo(() => {
        let result = enrollments;

        // Search filter: match against firstName, lastName, full name, email
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(e => {
                const name = (e.userName || '').toLowerCase();
                const email = (e.userEmail || '').toLowerCase();
                return name.includes(q) || email.includes(q);
            });
        }

        // Client-side sort by userName (API can't sort by enriched field)
        if (orderBy === 'userName') {
            result = [...result].sort((a, b) => {
                const aName = (a.userName || '').toLowerCase();
                const bName = (b.userName || '').toLowerCase();
                return order === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
            });
        }

        return result;
    }, [enrollments, searchQuery, orderBy, order]);

    const exportToCSV = () => {
        const headers = ['Name', 'Email', 'Kurs', 'Status', 'Fortschritt', 'Punktzahl', 'Eingeschrieben', 'Abgeschlossen'];
        const rows = enrollments.map(e => [
            e.userName || '',
            e.userEmail || '',
            e.courseName || '',
            getStatusLabel(e.status),
            `${Math.round(Number(e.progressPercentage) || 0)}%`,
            e.finalScore ? `${e.finalScore}%` : '-',
            formatDate(e.enrolledAt),
            formatDate(e.completedAt)
        ]);

        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `enrollments_${selectedCourse}_${Date.now()}.csv`;
        a.click();
    };

    return (
        <Box>
            {/* Aktionen brechen auf schmalen Viewports um; die Pill-Buttons selbst
                bleiben einzeilig (Design-Sweep 2026-10-28). */}
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: { xs: 1, md: 2 }, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => setCsvImportOpen(true)}
                    >
                        {translate('enrollments.manage.importCsv')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={enrollments.length === 0}
                    >
                        {translate('enrollments.manage.exportCsv')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<BulkEnrollIcon />}
                        onClick={() => setBulkEnrollOpen(true)}
                        sx={{ bgcolor: 'var(--oa-text)', '&:hover': { bgcolor: 'var(--oa-text)', opacity: 0.9 } }}
                    >
                        {translate('enrollments.manage.bulkEnroll')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<EnrollIcon />}
                        onClick={handleOpenEnrollDialog}
                    >
                        {translate('enrollments.manage.enrollUser')}
                    </Button>
                </Box>
            </Box>

            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab label="Einschreibungen" />
                <Tab label={`Anfragen${pendingRequests.length > 0 && activeTab !== 1 ? ` (${pendingRequests.length})` : ''}`} icon={<PendingIcon />} iconPosition="start" />
            </Tabs>

            {activeTab === 0 && (
            <>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    {/* Filterzeile: auf schmalen Viewports volle Breite pro Feld statt
                        einer 750px-Zeile, die rechts aus der Karte lief. */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 100%', md: '0 0 auto' }, minWidth: 0 } }}>
                        <FormControl sx={{ minWidth: { md: 300 } }}>
                            <InputLabel>{translate('enrollments.manage.selectCourse')}</InputLabel>
                            <Select
                                value={selectedCourse}
                                label={translate('enrollments.manage.selectCourse')}
                                onChange={(e) => { setSelectedCourse(e.target.value as number); setPage(1); }}
                            >
                                <MenuItem value="all">{translate('enrollments.manage.allCourses')}</MenuItem>
                                {courses.map((course) => (
                                    <MenuItem key={course.id} value={course.id}>
                                        {course.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: { md: 200 } }}>
                            <InputLabel>{translate('enrollments.manage.statusFilter')}</InputLabel>
                            <Select
                                value={statusFilter}
                                label={translate('enrollments.manage.statusFilter')}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                            >
                                <MenuItem value="all">{translate('enrollments.manage.statusAll')}</MenuItem>
                                <MenuItem value="enrolled">{translate('enrollments.manage.statusEnrolled')}</MenuItem>
                                <MenuItem value="in_progress">{translate('enrollments.manage.statusInProgress')}</MenuItem>
                                <MenuItem value="completed">{translate('enrollments.manage.statusCompleted')}</MenuItem>
                                <MenuItem value="failed">{translate('enrollments.manage.statusFailed')}</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            placeholder="Benutzer suchen..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ minWidth: { md: 250 } }}
                            InputProps={{
                                startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                            }}
                        />

                        <IconButton onClick={loadEnrollments} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>

            {loading ? (
                <Box sx={{ py: 5 }}>
                    <LinearProgress />
                    <Typography textAlign="center" sx={{ mt: 2 }}>
                        {translate('enrollments.manage.loading')}
                    </Typography>
                </Box>
            ) : filteredEnrollments.length === 0 ? (
                <Alert severity="info">
                    {searchQuery ? 'Keine Benutzer gefunden.' : translate('enrollments.manage.noEnrollments')}
                </Alert>
            ) : (
                <>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'userName'}
                                        direction={orderBy === 'userName' ? order : 'asc'}
                                        onClick={() => handleSort('userName')}
                                    >
                                        {translate('enrollments.manage.table.name')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('enrollments.manage.table.email')}</TableCell>
                                <TableCell>{translate('enrollments.manage.table.course')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={() => handleSort('status')}
                                    >
                                        {translate('enrollments.manage.table.status')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'progressPercentage'}
                                        direction={orderBy === 'progressPercentage' ? order : 'asc'}
                                        onClick={() => handleSort('progressPercentage')}
                                    >
                                        {translate('enrollments.manage.table.progress')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('enrollments.manage.table.score')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'enrolledAt'}
                                        direction={orderBy === 'enrolledAt' ? order : 'asc'}
                                        onClick={() => handleSort('enrolledAt')}
                                    >
                                        {translate('enrollments.manage.table.enrolled')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('enrollments.manage.table.started')}</TableCell>
                                <TableCell>{translate('enrollments.manage.table.completed')}</TableCell>
                                <TableCell>{translate('enrollments.manage.table.time')}</TableCell>
                                <TableCell align="right">{translate('enrollments.manage.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEnrollments.map((enrollment) => (
                                <TableRow key={enrollment.id}>
                                    <TableCell>{enrollment.userName || `User ${enrollment.userId}`}</TableCell>
                                    <TableCell>{enrollment.userEmail || '-'}</TableCell>
                                    <TableCell>{enrollment.courseName || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(enrollment.status)}
                                            color={getStatusColor(enrollment.status) as any}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={enrollment.progressPercentage || 0}
                                                sx={{ width: 100 }}
                                            />
                                            <Typography variant="body2">
                                                {Math.round(Number(enrollment.progressPercentage) || 0)}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {enrollment.finalScore ? `${enrollment.finalScore}%` : '-'}
                                    </TableCell>
                                    <TableCell>{formatDate(enrollment.enrolledAt)}</TableCell>
                                    <TableCell>{formatDate(enrollment.startedAt)}</TableCell>
                                    <TableCell>{formatDate(enrollment.completedAt)}</TableCell>
                                    <TableCell>{formatTime(enrollment.totalTimeSpent)}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(enrollment)}
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {((page - 1) * perPage) + 1}–{Math.min(page * perPage, total)} von {total}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Select
                                size="small"
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                sx={{ minWidth: 80 }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                                {translate('ra.navigation.previous')}
                            </Button>
                            <Typography variant="body2">
                                {page} / {Math.ceil(total / perPage)}
                            </Typography>
                            <Button size="small" disabled={page * perPage >= total} onClick={() => setPage(p => p + 1)}>
                                {translate('ra.navigation.next')}
                            </Button>
                        </Box>
                    </Box>
                )}
                </>
            )}
            </>
            )}

            {activeTab === 1 && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <IconButton onClick={loadPendingRequests} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                    {requestsLoading ? (
                        <Box sx={{ py: 5 }}>
                            <LinearProgress />
                            <Typography textAlign="center" sx={{ mt: 2 }}>Anfragen werden geladen...</Typography>
                        </Box>
                    ) : pendingRequests.length === 0 ? (
                        <Alert severity="info">Keine ausstehenden Einschreibungsanfragen.</Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Benutzer</TableCell>
                                        <TableCell>Kurs</TableCell>
                                        <TableCell>Anfragedatum</TableCell>
                                        <TableCell align="right">Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingRequests.map((enrollment) => (
                                        <TableRow key={enrollment.id}>
                                            <TableCell>{enrollment.userName || `User ${enrollment.userId}`}</TableCell>
                                            <TableCell>{enrollment.courseName || '-'}</TableCell>
                                            <TableCell>{formatDate(enrollment.enrolledAt)}</TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() => handleApprove(enrollment)}
                                                    title="Genehmigen"
                                                >
                                                    <ApproveIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleOpenRejectDialog(enrollment)}
                                                    title="Ablehnen"
                                                >
                                                    <RejectIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Einschreibung ablehnen</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Anfrage von <strong>{rejectingEnrollment?.userName}</strong> für{' '}
                            <strong>{rejectingEnrollment?.courseName}</strong> ablehnen?
                        </Typography>
                        <TextField
                            label="Begründung (optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={rejectMessage}
                            onChange={(e) => setRejectMessage(e.target.value)}
                            placeholder="Grund für die Ablehnung (wird dem Benutzer mitgeteilt)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={handleReject} variant="contained" color="error">
                        Ablehnen
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Enroll Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{translate('enrollments.manage.dialog.title')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <Autocomplete
                            options={users}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            getOptionLabel={(option) => {
                                const name = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                                return name ? `${name} (${option.email})` : option.email;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={translate('enrollments.manage.dialog.user')}
                                    required
                                    helperText={translate('enrollments.manage.dialog.userHelp')}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                        />

                        <FormControl fullWidth required>
                            <InputLabel>{translate('enrollments.manage.dialog.course')}</InputLabel>
                            <Select
                                value={selectedCourseForEnroll}
                                label={translate('enrollments.manage.dialog.course')}
                                onChange={(e) => setSelectedCourseForEnroll(e.target.value as number)}
                            >
                                {courses.map((course) => (
                                    <MenuItem key={course.id} value={course.id}>
                                        {course.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{translate('ra.action.cancel')}</Button>
                    <Button
                        onClick={handleEnroll}
                        variant="contained"
                        disabled={!selectedUser || !selectedCourseForEnroll}
                    >
                        {translate('enrollments.manage.dialog.enroll')}
                    </Button>
                </DialogActions>
            </Dialog>

            <CsvImportDialog
                open={csvImportOpen}
                onClose={() => setCsvImportOpen(false)}
                courses={courses}
                onSuccess={loadEnrollments}
            />

            <BulkEnrollmentDialog
                open={bulkEnrollOpen}
                onClose={() => setBulkEnrollOpen(false)}
                courses={courses}
                initialCourseId={typeof selectedCourse === 'number' ? selectedCourse : ''}
                onSuccess={loadEnrollments}
            />
        </Box>
    );
};

export default EnrollmentManagement;
